import React, { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// Redux slices
import { setSelected, updateImageData } from './mediaSlice';
import { updateObjectData } from './objectSlice';

// SemanticUI components
import { CardHeader, CardContent, Card, Image, Grid, GridRow, GridColumn, Icon, LabelGroup, Label, FormInput, Form, FormGroup, FormSelect, Header, HeaderContent } from 'semantic-ui-react'

// Styles
import './object.css';

import DragDropUploader from './DragDropUploader';

/**
 * https://react-dnd.github.io/react-dnd/examples/sortable/simple
 * https://codesandbox.io/p/sandbox/github/react-dnd/react-dnd/tree/gh-pages/examples_js/04-sortable/simple?file=%2Fpackage.json&from-embed
 * 
 * https://github.com/react-grid-layout/react-draggable/blob/master/example/example.js
 * https://react-grid-layout.github.io/react-draggable/example/
 * https://www.npmjs.com/package/react-draggable
 */



/**
 * 
 * @param {object} image 
 * @returns 
 */
const Max2MediaImage = ({image}) => {
  // global state
  const dispatch = useDispatch();
  const selectedImg = useSelector((state) => state.media.selected);
  const isEditMode  = useSelector((state) => state.object.sections.Media.edit);
  
  //
  const url = image.File.Path_Thumbnails + '/' + image.File.Filename;
  const isSelected = (selectedImg && typeof selectedImg === 'object' && typeof selectedImg.ImageId !== 'undefined' && selectedImg.ImageId === image.ImageId) ? true : false;
  const labelStyle = isSelected ? {width:'40px', backgroundColor: 'rgb(255 120 120 / .8)'} : {width:'40px', backgroundColor: 'rgb(255 255 255 / .8)'};

  // Edit mode
  if (isEditMode) {
    return (
      <GridColumn key={image.id}>
        { isSelected && (<Image src={url} rounded style={{boxShadow: "5px 5px 15px rgba(0, 0, 0, 0.5)", cursor: 'pointer'}} />)}
        {!isSelected && (<Image src={url} rounded bordered style={{cursor: 'pointer'}} onClick={() => dispatch(setSelected(image))} />)}
        <div style={{height: '30px', textAlign: 'center', position: 'absolute', top: '20px', left: '20px'}}>
          <span style={{position: 'absolute', bottom: '5px'}}>
            <Label circular style={labelStyle}>{image.Position}</Label>
          </span>
        </div>
        <div style={{height: '30px', textAlign: 'center', position: 'absolute', top: '20px', right: '75px'}}>
          <span style={{position: 'absolute', bottom: '5px'}}>
            <Label circular style={{width:'40px', backgroundColor: 'rgb(255 255 255 / .8)'}}>
              <Icon link name='trash alternate outline' style={{paddingLeft: '9px'}} />
            </Label>
          </span>
        </div>
      </GridColumn>
    );
  }

  // readonly mode
  return (
    <GridColumn key={image.id}>
      { isSelected && (<Image src={url} rounded style={{boxShadow: "5px 5px 15px rgba(0, 0, 0, 0.5)", cursor: 'pointer'}} />)}
      {!isSelected && (<Image src={url} rounded bordered style={{cursor: 'pointer'}} onClick={() => dispatch(setSelected(image))} />)}
      <div style={{height: '30px', textAlign: 'center', position: 'absolute', top: '20px', left: '20px'}}>
        <span style={{position: 'absolute', bottom: '5px'}}>
          <Label circular style={labelStyle}>{image.Position}</Label>
        </span>
      </div>
    </GridColumn>
  );
};



/**
 * 
 * @param {object} image
 * @returns 
 */
const Max2MediaPreview = ({image}) => {
  // global state
  const dispatch = useDispatch();
  const isEditMode  = useSelector((state) => state.object.sections.Media.edit);
  const selectedImg = useSelector((state) => state.media.selected);

  //
  const url = image.File.Path_Images + '/' + image.File.Filename;



  /**
   * 
   * @param {string} target 
   * @param {string} subTarget 
   * @param {string} value 
   */
  const handleInputChange = (target, subTarget, value) => {
    dispatch(updateImageData({target: target, subTarget: subTarget, value: value}));
    dispatch(updateObjectData({section: 'Images', key: selectedImg.Position, target: target, subTarget: subTarget, value: value}));
  };



  /**
   * @param integer widthPx
   * @param integer heightPx
   * @param integer dpi
   * 
   * @return object
   */
  function calculatePrintSize(widthPx, heightPx, dpi) {
    if (!widthPx || !heightPx || !dpi) {
      return false
    }

    // 1 Zoll (inch) = 2.54 cm
    const zollInCm = 2.54;

    // Breite und Höhe in Zoll berechnen
    const breiteInZoll = parseInt(widthPx) / dpi;
    const hoeheInZoll = parseInt(heightPx) / dpi;

    // Breite und Höhe in Zentimeter umrechnen
    const breiteInCm = breiteInZoll * zollInCm;
    const hoeheInCm = hoeheInZoll * zollInCm;

    return {
        width: breiteInCm.toFixed(2),
        height: hoeheInCm.toFixed(2)
    };
  }
  
  // build print size string
  let printsize = calculatePrintSize(image.File.Width, image.File.Height, image.File.Dpi);
  if (printsize) {
    printsize = printsize.width + ' x ' + printsize.height;
  }

  // Filename length
  const len = image.File.Filename.length;


  const optionsTags = [
    {key:29, value: 29, text: "Appartement"},
    {key:66, value: 66, text: "Appartement/2 Schlafzimmer"},
    {key:69, value: 69, text: "Appartement/Meerblick"},
    {key:74, value: 74, text: "Budgetzimmer"},
    {key:30, value: 30, text: "Bungalow"},
    {key:73, value: 73, text: "Chalet"},
    {key:47, value: 47, text: "Clubzimmer"},
    {key:31, value: 31, text: "Deluxe"},
    {key:27, value: 27, text: "Doppelzimmer"},
    {key:62, value: 62, text: "Doppelzimmer/Seitlicher Meerblick"},
    {key:61, value: 61, text: "Doppelzimmer zur Alleinbenutzung"},
    {key:72, value: 72, text: "Dreibett"},
    {key:28, value: 28, text: "Einzelzimmer"},
    {key:70, value: 70, text: "Einzelzimmer im Mehrbettzimmer"},
    {key:63, value: 63, text: "Einzelzimmer Meerblick"},
    {key:65, value: 65, text: "Einzelzimmer mit Kind"},
    {key:32, value: 32, text: "Familienzimmer"},
    {key:64, value: 64, text: "Familienzimmer/2 Schlafzimmer"},
    {key:71, value: 71, text: "Ferienhaus"},
    {key:68, value: 68, text: "Finca"},
    {key:33, value: 33, text: "Juniorsuite"},
    {key:46, value: 46, text: "Komfortzimmer"},
    {key:67, value: 67, text: "Meerblickzimmer"},
    {key:44, value: 44, text: "Meerblickzimmer"},
    {key:36, value: 36, text: "Mehrbettzimmer"},
    {key:60, value: 60, text: "Promotionzimmer"},
    {key:53, value: 53, text: "Startbild"},
    {key:38, value: 38, text: "Studio"},
    {key:34, value: 34, text: "Suite"},
    {key:45, value: 45, text: "Superiorzimmer"},
    {key:48, value: 48, text: "Villa"},
    {key:49, value: 49, text: "Wasserbungalow"},
  ];

  if (isEditMode) {
    return (
      <Card fluid raised className='image-details'>
        <Image src={url} wrapped />
        <div style={{position: 'absolute', right: '5px', top: '5px'}}>
          <Icon link name='times' onClick={()=>dispatch(setSelected(null))} /> 
        </div>
        <CardContent>
          {len <= 45 &&              ( <CardHeader style={{fontSize: '1.2em'}}>{image.File.Filename}</CardHeader>)}
          {len  > 45 && len <= 60 && ( <CardHeader style={{fontSize: '1.0em'}}>{image.File.Filename}</CardHeader>)}
          {len  > 60 && len <= 75 && ( <CardHeader style={{fontSize: '0.8em'}}>{image.File.Filename}</CardHeader>)}
          {len  > 75 && len <= 90 && ( <CardHeader style={{fontSize: '0.7em'}}>{image.File.Filename}</CardHeader>)}
          {len  > 90 &&              ( <CardHeader style={{fontSize: '0.6em'}}>{image.File.Filename}</CardHeader>)}
          <CardHeader style={{fontSize: '0.8em'}}>({image.File.OriginalFilename})</CardHeader>
        </CardContent>
        <CardContent>
          <Form>
              <FormGroup widths='equal'>
                <FormInput fluid label="Breite (px)"  placeholder="Breite"  value={image.File.Width}  onChange={(e, data) => handleInputChange('File', 'Width', data.value)} />
                <FormInput fluid label="Höhe (px)"    placeholder="Höhe"    value={image.File.Height} onChange={(e, data) => handleInputChange('File', 'Height', data.value)} />
              </FormGroup>
              <FormSelect fluid search multiple label="Schlagworte" placeholder='Bitte wählen' value={image.Tags} options={optionsTags} onChange={(e, data) => handleInputChange('Tags', null, data.value)} />
              <FormInput fluid label="Auflösung (DPI)" placeholder="DPI"    value={image.File.Dpi}  onChange={(e, data) => handleInputChange('File', 'Dpi', data.value)} />
              <FormInput fluid label="Herkunft" placeholder="Herkunft"      value={image.Origin}    onChange={(e, data) => handleInputChange('Origin', null, data.value)} />
              <FormInput fluid label="Veranstaltercode" placeholder="Code"  value={image.Code}      onChange={(e, data) => handleInputChange('Code', null, data.value)} />
              <FormInput fluid label="Copyright" placeholder="Copyright"    value={image.Copyright} onChange={(e, data) => handleInputChange('Copyright', null, data.value)} />
          </Form>
        </CardContent>
      </Card>
    )  
  }

  return (
    <Card fluid raised className='image-details'>
      <Image src={url} wrapped />
      <div style={{position: 'absolute', right: '5px', top: '5px'}}>
        <Icon link name='times' onClick={()=>dispatch(setSelected(null))} /> 
      </div>
      <CardContent>
        {len <= 45 &&              ( <CardHeader style={{fontSize: '1.2em'}}>{image.File.Filename}</CardHeader>)}
        {len  > 45 && len <= 60 && ( <CardHeader style={{fontSize: '1.0em'}}>{image.File.Filename}</CardHeader>)}
        {len  > 60 && len <= 75 && ( <CardHeader style={{fontSize: '0.8em'}}>{image.File.Filename}</CardHeader>)}
        {len  > 75 && len <= 90 && ( <CardHeader style={{fontSize: '0.7em'}}>{image.File.Filename}</CardHeader>)}
        {len  > 90 &&              ( <CardHeader style={{fontSize: '0.6em'}}>{image.File.Filename}</CardHeader>)}
        <CardHeader style={{fontSize: '0.8em'}}>({image.File.OriginalFilename})</CardHeader>
      </CardContent>
      <CardContent>
        <Grid>
          <GridRow className='media_ro_row'>
            <GridColumn width={5} className='media_ro_label' textAlign='right'>Größe (Pixel):</GridColumn>
            <GridColumn width={11}>{image.File.Width} x {image.File.Height}</GridColumn>
          </GridRow>
          <GridRow className='media_ro_row'>
            <GridColumn width={5} className='media_ro_label' textAlign='right'>Auflösung (DPI):</GridColumn>
            <GridColumn width={11}>{image.File.Dpi}</GridColumn>
          </GridRow>
          <GridRow className='media_ro_row'>
            <GridColumn width={5} className='media_ro_label' textAlign='right'>Größe Print (cm):</GridColumn>
            <GridColumn width={11}>{printsize}</GridColumn>
          </GridRow>
          <GridRow className='media_ro_row'>
            <GridColumn width={5} className='media_ro_label' textAlign='right'>Dateigröße:</GridColumn>
            <GridColumn width={11}>{(parseInt(image.File.Filesize)/1024).toFixed(0)} kB</GridColumn>
          </GridRow>
        </Grid>
        
        <Grid>
          <GridRow className='media_ro_row'>
            <GridColumn width={5} className='media_ro_label' textAlign='right'>Schlagworte</GridColumn>
            <GridColumn width={11}>
              <LabelGroup>
                {image.Tags.map((tag, tagKey) => {
                  let tagName = '';
                  optionsTags.forEach(option => {
                    if (option.key === tag) {
                      tagName = option.text;
                    }
                  });
                  return <Label circular key={tagKey}>&nbsp;{tagName}&nbsp;</Label>
                })}
              </LabelGroup>
            </GridColumn>
          </GridRow>
        </Grid>
        
        <Grid>
          {image.Origin && (<GridRow className='media_ro_row'><GridColumn width={5} className='media_ro_label' textAlign='right'>Herkunft</GridColumn><GridColumn width={11}>{image.Origin}</GridColumn></GridRow>)}
          {image.Code && (<GridRow className='media_ro_row'><GridColumn width={5} className='media_ro_label' textAlign='right'>Veranstaltercode</GridColumn><GridColumn width={11}>{image.Code}</GridColumn></GridRow>)}
          {image.Copyright && (<GridRow className='media_ro_row'><GridColumn width={5} className='media_ro_label' textAlign='right'>Copyright</GridColumn><GridColumn width={11}>{image.Copyright}</GridColumn></GridRow>)}
        </Grid>
      </CardContent>
    </Card>
  )
}



/**
 * 
 * @returns 
 */
const Max2Media = () => {
  // global State
  const selectedImg = useSelector((state) => state.media.selected);
  const object      = useSelector((state) => state.object.data);
  const isEditMode  = useSelector((state) => state.object.sections.Media.edit);

 
  const renderImage = useCallback((image) => {
    return (
      <Max2MediaImage key={image.ImageId} image={image} />
    )
  }, [])
  
  if (selectedImg && typeof selectedImg === 'object' && typeof selectedImg.ImageId !== 'undefined') {
    return (
      <Grid columns={2}>
        <GridColumn width={6}>
          <Max2MediaPreview image={selectedImg} />
        </GridColumn>
        <GridColumn width={10}>
          <Grid columns={5}>
            {object.Images.map((image) => renderImage(image))}
          </Grid>
        </GridColumn>
        {isEditMode && (
          <GridColumn width={16} textAlign='right'>
            <DragDropUploader uploadUrl="/api/upload" />
          </GridColumn>
        )}
      </Grid>
    );
  } else {
    return (
      <Grid columns={8}>
        {object.Images.length > 0 
          ? <>{object.Images.map((image, i) => renderImage(image, i))}</>
          : <><br/><Header as='h4' textAlign='center'><Icon name='times circle outline' /><HeaderContent>Keine Einträge vorhanden</HeaderContent></Header><br /></>
        }
        {isEditMode && (
          <GridColumn width={16} textAlign='right'>
            <DragDropUploader uploadUrl="/api/upload" />
          </GridColumn>
        )}
      </Grid>
    )
  }
}

export default Max2Media;
